import React, { useState, useEffect, createElement} from "react";
import { useTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { removeChat } from "../../util/chatBotUtils";
import { customerSupportLink, onCloseRevealRedirectionLink, handleOnCloseEvent, revealRedirectionLink } from "../../util/helpURLNavigation";
import { TextLink } from "@vds/buttons";
import Cookies from "js-cookie";
import { resetRedux,removeTheToken,checkImpersonateUser } from "../../util/util";
import { changeLanguageHandler } from "../../util/userLanguageUtils";
import RedBallFallbackPage from "./RedBallFallbackPage";
import CPFallbackPage from "./CPFallbackPage";
import { REDBALL_FEATURE_TOGGLE, suspendedFinancialStatuses, terminatedFinancialStatuses } from "../../constants/appConstants";
import { getCountryContact } from "../../components/common/CountryCode";
import { COMMON_CONSTANTS, REDBALL_INACTIVE_ACCOUNT_CONSTANTS } from "../../constants/StringConstants";
import { Colors } from "../../constants/Colors";
import EmailLink from "../../components/common/EmailLink";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";

function InactiveAccount(props) {
  const { logout } = useAuth0();
  const [title, setTitle] = useState("");
  const [pageContent, setPageContent] = useState("");
  // START - REDBALL CONFIG 
  const redBallCustomer = REDBALL_FEATURE_TOGGLE && (terminatedFinancialStatuses.includes(props.accountFinancialStatus?.toLowerCase()) || suspendedFinancialStatuses.includes(props.accountFinancialStatus?.toLowerCase()));
  // START - REDBALL CONFIG 
  const { t, i18n } = useTranslation(redBallCustomer ? "redBallTranslation" : "loginTranslation");

  const { accountUserTokenDetails } = useSelector((state) => state.ACCOUNT);
  const isNACOUNTRY = accountUserTokenDetails?.isNA;

  useEffect(() => {
    removeChat(props.regionId, props.emailAddress);
  }, []);

  useEffect(() => {
    if (Cookies.get("userLanguage")) {
      i18n.changeLanguage(changeLanguageHandler(Cookies.get("userLanguage")));
    }
    redBallCustomer && getPageContent();

    return () => {
      resetRedux(); 
      removeTheToken(); 
      sessionStorage.clear();
    }
  }, []);

  useEffect(() => {
    getPageContent();
  }, [props.windowSize.width > 399]);

  useEffect(() => {
    document.title = t(`${props.title}`);
  }, [props.title]);

  function getSuspendedContactText({ isNACOUNTRY, windowSize, userLanguage, t, contactDetails }) {
    if (isNACOUNTRY) {
      if (windowSize.width > 399 && userLanguage !== "fr-FR") {
        return (
          <>
            {t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.PHONE_ADMIN_CONTACT)}
            <br />
            {t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.ON)} {t(contactDetails?.phone)}
          </>
        );
      }
      return (
        <>
          {t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.NA_ADMIN_CONTACT_TEXT)} {t(contactDetails?.phone)}
        </>
      );
    }
    return (
      <>
        {t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.EMEA_ADMIN_CONTACT_TEXT)}:
      </>
    );
  };

  const getPageContent = () => {
    const { regionId, windowSize, userLanguage, accountFinancialStatus } = props;
    const contactDetails = getCountryContact(regionId);
    const isSuspended = suspendedFinancialStatuses.includes(accountFinancialStatus?.toLowerCase());
    const isTerminated = terminatedFinancialStatuses.includes(accountFinancialStatus?.toLowerCase());
  
    const suspendedContactText = getSuspendedContactText({
      isNACOUNTRY,
      windowSize,
      userLanguage,
      t,
      contactDetails,
    });
  
    const terminatedContactText = REDBALL_INACTIVE_ACCOUNT_CONSTANTS.REDBALL_CONTACT_US;
  
    if (isSuspended) {
      setTitle(t(COMMON_CONSTANTS.ACCOUNT_SUSPENDED));
    } else if (isTerminated) {
      setTitle(t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.ACCOUNT_TERMINATED));
    }
  
    if (isTerminated) {
      setPageContent(
        isNACOUNTRY
          ? <>{t(terminatedContactText)} {t(contactDetails?.phone)}.</>
          : <>{t(terminatedContactText)}: {renderContactBulletPoints(regionId, true)}</>
      );
    } else if (isSuspended) {
      const contactText = isNACOUNTRY
        ? <>{suspendedContactText}.</>
        : <>{suspendedContactText} {renderContactBulletPoints(regionId)}</>;
  
      setPageContent(
        createElement(
          "div",
          null,
          createElement("p", null, t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.SUSPENDED_MSG_ONE)),
          createElement("p", null, t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.SUSPENDED_MSG_TWO)),
          createElement("p", null, contactText)
        )
      );
    }
  };

  const renderContactBulletPoints = (regionId, isMarginTop = false) => {
    return (
      <ul style={{ paddingLeft: "20px", marginTop: isMarginTop ? "20px" : "5px" }}>
        <li key={COMMON_CONSTANTS.EMAIL}>
          <div size="small">
            {t(COMMON_CONSTANTS.EMAIL)}:&nbsp;
            <EmailLink email={t(getCountryContact(regionId)?.email)} color={Colors.frenchBlue} fontWeight={400} />
          </div>
        </li>
        <li key={COMMON_CONSTANTS.PHONE}>
          <div size="small">
            {t(COMMON_CONSTANTS.PHONE)}: {t(getCountryContact(regionId)?.phone)}
          </div>
        </li>
      </ul>
    )
  }

  const handleClose = () => {
    if (REDBALL_FEATURE_TOGGLE && props.accountFinancialStatus != null && props.accountFinancialStatus != "") {
      checkImpersonateUser() ?  window.open(revealRedirectionLink(), "_self") : logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      handleOnCloseEvent(onCloseRevealRedirectionLink());
    }
  };
  return (
    <>
      {redBallCustomer ? (
        <RedBallFallbackPage
          windowSize={props.windowSize.width}
          ariaLabel={title}
          pageTitle={title}
          pageContent={<>{pageContent}</>}
          buttonId="redball-back-to-login-button"
          buttonHandler={() => handleClose()}
          buttonName={t(REDBALL_INACTIVE_ACCOUNT_CONSTANTS.LOGIN_BUTTON_TEXT)}
        />
      ) : (
        <CPFallbackPage
          windowSize={props.windowSize.width}
          ariaLabel="Inactive Account"
          pageTitle={t("Inactive Account")}
          pageContent={
            <>
              {t("Invalid Contract ID. Please contact")}{" "}
              <TextLink
                id="goto-reveal-customer-support-link"
                ariaLabel="customer support link"
                href={customerSupportLink()}
                style={{ color: "#0077B4", borderColor: "#0077B4" }}
              >
                {t("customer support.")}
                {Cookies.get("userLanguage") === "nl-NL" ? "." : ""}
              </TextLink>
            </>
          }
          buttonId="close-button"
          buttonHandler={() => handleClose()}
          buttonName={t("CLOSE")}
          buttonAriaLabel="close this tab"
        />
      )}
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    windowSize: state.ACCOUNT.windowSize,
    emailAddress: state.ACCOUNT.emailAddress,
    userLanguage: state.ACCOUNT.userLanguage,
    regionId: state.ACCOUNT.regionId,
    accountFinancialStatus: state.ACCOUNT.accountFinancialStatus,
  };
};

InactiveAccount.propTypes = {
  regionId: PropTypes.string,
  emailAddress: PropTypes.string,
  windowSize: PropTypes.object,
  userLanguage: PropTypes.string,
  accountFinancialStatus: PropTypes.string,
  title: PropTypes.string,
};

export default connect(mapStateToProps)(InactiveAccount);